import React from 'react';
import { Search, Plus, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface EmptyStateProps {
  type: 'no-results' | 'no-listings' | 'filtered';
  onClearFilters?: () => void;
  onCreateListing?: () => void;
  hasFilters?: boolean;
  assetType?: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
}

const assetLabels: Record<string, { plural: string; singular: string }> = {
  helicopter: { plural: 'helicopters', singular: 'helicopter' },
  engine: { plural: 'engines', singular: 'engine' },
  parts: { plural: 'parts & tools', singular: 'part' },
  jobs: { plural: 'jobs', singular: 'job' },
  services: { plural: 'services', singular: 'service' },
};

const EmptyState: React.FC<EmptyStateProps> = ({
  type,
  onClearFilters,
  onCreateListing,
  hasFilters = false,
  assetType = 'helicopter'
}) => {
  const label = assetLabels[assetType] || assetLabels.helicopter;

  const getContent = () => {
    switch (type) {
      case 'no-results':
        return {
          icon: <Search className="h-12 w-12 text-muted-foreground mb-4" />,
          title: `No ${label.plural} found`,
          description: hasFilters 
            ? `No ${label.plural} match your current filters. Try adjusting your search criteria.`
            : `No ${label.plural} match your search. Try different keywords.`,
          action: hasFilters && onClearFilters && (
            <Button variant="outline" onClick={onClearFilters}>
              <Filter className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
          )
        };

      case 'filtered':
        return {
          icon: <Filter className="h-12 w-12 text-muted-foreground mb-4" />,
          title: 'No results with these filters',
          description: 'Try adjusting your price range, seating requirements, or other filters.',
          action: onClearFilters && (
            <Button variant="outline" onClick={onClearFilters}>
              <Filter className="h-4 w-4 mr-2" />
              Clear All Filters
            </Button>
          )
        };

      case 'no-listings':
      default:
        return {
          icon: <Plus className="h-12 w-12 text-muted-foreground mb-4" />,
          title: 'No listings yet',
          description: `Be the first to list a ${label.singular} for sale or rent on our marketplace.`,
          action: onCreateListing && (
            <Button onClick={onCreateListing}>
              <Plus className="h-4 w-4 mr-2" />
              Create Listing
            </Button>
          )
        };
    }
  };

  const content = getContent();

  return (
    <div className="text-center py-12 px-4">
      <div className="max-w-sm mx-auto">
        {content.icon}
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {content.title}
        </h3>
        <p className="text-muted-foreground mb-6 text-sm">
          {content.description}
        </p>
        {content.action}
      </div>
    </div>
  );
};

export default EmptyState;
